import { detectionsApi } from '@/api/detections.api';
import { ActionButton } from '@/components/common/ActionButton';
import type { DetectionModel, HoleSet } from '@/types/detection.types';
import { Eye, Trash } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ConfirmDeleteDialog } from '../dialogs/ConfirmDeleteDialog';
import { JSONViewDialog } from '../dialogs/JSONViewDialog';

interface DetectionActionsProps {
  detection: DetectionModel;
  onDelete: (id: string) => void;
}

export const DetectionActions: FC<DetectionActionsProps> = ({ detection, onDelete }) => {
  const { t } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSourceDialog, setShowSourceDialog] = useState(false);
  const [sourceData, setSourceData] = useState<HoleSet | null>(null);
  const [isLoadingSource, setIsLoadingSource] = useState(false);

  const handleViewSource = async () => {
    setIsLoadingSource(true);
    try {
      const data = await detectionsApi.getSource(detection.id);
      setSourceData(data);
      setShowSourceDialog(true);
    } catch (_error) {
      toast.error(t('detections.failedToLoad'));
    } finally {
      setIsLoadingSource(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    onDelete(detection.id);
    setShowDeleteDialog(false);
  };

  return (
    <>
      <div className="flex items-center gap-1">
        {/* View source (HoleSet for this orientation) */}
        <ActionButton
          variant="ghost"
          iconOnly={true}
          icon={<Eye size={16} aria-hidden="true" />}
          onClick={handleViewSource}
          loading={isLoadingSource}
          aria-label={t('detections.viewHoles')}
          title={t('detections.viewHoles')}
        />

        {/* Delete */}
        <ActionButton
          variant="danger-ghost"
          iconOnly={true}
          icon={<Trash size={16} aria-hidden="true" />}
          onClick={handleDelete}
          aria-label={t('detections.deleteDetection')}
          title={t('detections.deleteDetection')}
        />
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDeleteDialog
        visible={showDeleteDialog}
        title={t('detections.deleteDetection')}
        message={t('detections.deleteConfirm', { orientation: detection.orientation })}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {/* Source JSON dialog (HoleSet for this orientation) */}
      {sourceData && (
        <JSONViewDialog
          visible={showSourceDialog}
          title={`${t('detections.holes')}: ${detection.orientation}`}
          jsonData={sourceData}
          onClose={() => setShowSourceDialog(false)}
          downloadFilename={`detection_${detection.orientation}_holes.json`}
        />
      )}
    </>
  );
};
